import { Bell, Monitor, Moon, Palette, SlidersHorizontal, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useEffect } from 'react';
import { Navigate, Outlet, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { findAppByProductKey } from '../../lib/app-registry';
import type { BetterAuthClient } from '../../lib/betterAuthClient';
import {
  getAppPersona,
  WORKSPACE_FEATURE_DESCRIPTIONS,
  WORKSPACE_FEATURE_LABELS,
} from '../../lib/feature-flags';
import type { PushDriver } from '../../lib/push-setup';
import { trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Switch } from '../../ui/switch';
import { EmailVerificationNotice } from '../auth/EmailVerificationNotice';
import { AiCreditsPanel } from '../billing/AiCreditsPanel';
import { InvoiceHistoryTable } from '../billing/InvoiceHistoryTable';
import { type ProductKey, SubscriptionBillingPanel } from '../billing/SubscriptionBillingPanel';
import { NotificationPreferencesPanel } from '../notifications/NotificationPreferencesPanel';
import { AiProviderKeysSection } from './AiProviderKeysSection';
import { ConnectedAccountsSection } from './ConnectedAccountsSection';
import { type SettingsNavItem, SettingsSidebar } from './SettingsSidebar';
import { WorkspaceMembersSection } from './WorkspaceMembersSection';

/**
 * Mailbox/OAuth account connection. Omitted entirely by products that never connect a
 * third-party account — Skill Viper signs in with Google but has no mailbox to attach, so it
 * ships without the Accounts tab rather than showing an empty one.
 */
export interface ConnectAccountsConfig {
  /** Selects the correct branded origin for the post-OAuth workspace settings return. */
  portal: 'clinic' | 'skill-viper';
}

export interface SettingsLayoutProps {
  productKey: ProductKey;
  productLabel: string;
  pushDriver: PushDriver;
  connectAccounts?: ConnectAccountsConfig;
  getAuthToken: () => Promise<string | null>;
  showDiagnostics?: boolean;
  /** Full custom list of nav items, completely overriding default settings items */
  navItems?: SettingsNavItem[];
  /** App-specific nav items injected into the standard settings sidebar */
  customNavItems?: SettingsNavItem[];
  /** Custom destination for the "Back to App" button */
  backTo?: string;
  /**
   * The portal's Better Auth client. Supplied to show the email-verification prompt, which
   * needs the signed-in address to resend to. Optional so a portal that has not wired it yet
   * keeps working — it simply shows no prompt.
   */
  authClient?: BetterAuthClient;
}

export interface SettingsOutletContext {
  productKey: ProductKey;
  productLabel: string;
  pushDriver: PushDriver;
  connectAccounts?: ConnectAccountsConfig;
  getAuthToken: () => Promise<string | null>;
  showDiagnostics?: boolean;
  isAdmin: boolean;
  isReadOnly: boolean;
  activeWorkspace?: { id: number; organizationId: number };
  currentUserId?: number;
}

export function SettingsLayout({
  productKey,
  productLabel,
  pushDriver,
  connectAccounts,
  getAuthToken,
  showDiagnostics = false,
  navItems,
  customNavItems,
  backTo,
  authClient,
}: SettingsLayoutProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { orgId, workspaceId: currentWorkspaceIdStr } = useParams<{
    orgId: string;
    workspaceId: string;
  }>();

  useEffect(() => {
    if (currentWorkspaceIdStr) {
      localStorage.setItem('workspaceId', currentWorkspaceIdStr);
    }
    if (orgId) {
      localStorage.setItem('organizationId', orgId);
    }
  }, [currentWorkspaceIdStr, orgId]);

  const utils = trpc.useUtils();
  const { data: me } = trpc.auth.me.useQuery();

  const activeWorkspace = me?.workspaces.find((w) => w.id.toString() === currentWorkspaceIdStr);
  const isAdmin = activeWorkspace?.role === 'admin';
  const isReadOnly = activeWorkspace?.role === 'viewer';

  useEffect(() => {
    const gmail = searchParams.get('gmail');
    const outlook = searchParams.get('outlook');
    const upgrade = searchParams.get('upgrade');
    if (!gmail && !outlook && !upgrade) return;

    // One handler for both providers. The connect flow reports back on `?gmail=` or
    // `?outlook=` (the adapter's own param), and previously only Gmail was read — an Outlook
    // connect, successful or not, said nothing at all.
    for (const [result, label] of [
      [gmail, 'Gmail'],
      [outlook, 'Outlook'],
    ] as const) {
      if (result === 'success') {
        toast.success(`${label} account connected.`);
        utils.gmail.getConnectedExternalAccounts.invalidate();
      } else if (result === 'error') {
        const message = searchParams.get('message') || 'Please try again.';
        const consentUrl = searchParams.get('consentUrl');

        // A tenant that requires administrator approval is a normal onboarding step, not a
        // fault — so it is named, and the member is handed the link to forward rather than
        // being left to work out what "access_denied" meant.
        if (searchParams.get('reason') === 'admin_consent_required' && consentUrl) {
          toast.error(`${label} needs your administrator's approval`, {
            description: `${message} Send them the approval link — it covers everyone at once.`,
            duration: 30000,
            action: {
              label: 'Copy approval link',
              onClick: () => {
                navigator.clipboard?.writeText(consentUrl);
                toast.success('Approval link copied.');
              },
            },
          });
        } else {
          toast.error(`Could not connect ${label}`, { description: message });
        }
      }
    }

    if (upgrade === 'success') {
      toast.success('Subscription upgraded successfully!');
      utils.auth.me.invalidate();
    }

    const next = new URLSearchParams(searchParams);
    for (const key of ['gmail', 'outlook', 'upgrade', 'message', 'reason', 'consentUrl']) {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, utils]);

  const contextValue: SettingsOutletContext = {
    productKey,
    productLabel,
    pushDriver,
    connectAccounts,
    getAuthToken,
    showDiagnostics,
    isAdmin: !!isAdmin,
    isReadOnly: !!isReadOnly,
    activeWorkspace,
    currentUserId: me?.uid,
  };

  return (
    <div className="flex h-svh w-full overflow-hidden bg-background">
      <SettingsSidebar
        productLabel={productLabel}
        isAdmin={!!isAdmin}
        showAccounts={!!connectAccounts}
        items={navItems}
        customNavItems={customNavItems}
        backTo={backTo}
      />
      <main className="flex-1 overflow-y-auto min-w-0 p-8">
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
          {authClient && (
            <EmailVerificationNotice emailVerified={me?.emailVerified} authClient={authClient} />
          )}
          <Outlet context={contextValue} />
        </div>
      </main>
    </div>
  );
}

// ─── Sub-Route Section Components ─────────────────────────────────────────────

export function AccountsSettingsSection() {
  const ctx = useOutletContext<SettingsOutletContext>();
  if (!ctx.connectAccounts) {
    return <Navigate to="../appearance" replace />;
  }
  return (
    <ConnectedAccountsSection
      portal={ctx.connectAccounts.portal}
      isReadOnly={ctx.isReadOnly}
      getAuthToken={ctx.getAuthToken}
    />
  );
}

export function AppearanceSettingsSection() {
  const { theme, setTheme } = useTheme();

  const themes = [
    {
      value: 'light',
      title: 'Light',
      description: 'Clean, high-contrast light theme for bright environments.',
      icon: Sun,
    },
    {
      value: 'dark',
      title: 'Dark',
      description: 'Sleek, low-glare dark mode tailored for late-night sessions.',
      icon: Moon,
    },
    {
      value: 'system',
      title: 'System Default',
      description: 'Automatically synchronizes with your operating system preferences.',
      icon: Monitor,
    },
  ];

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      <CardHeader className="border-b border-border/50 bg-muted/30">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-primary" />
          <CardTitle>Appearance</CardTitle>
        </div>
        <CardDescription>
          Customize how your workspace looks and feels. Select a theme option below.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <div className="grid gap-4 sm:grid-cols-3">
          {themes.map((t) => {
            const Icon = t.icon;
            const isSelected = (theme ?? 'system') === t.value;

            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTheme(t.value)}
                className={cn(
                  'flex flex-col justify-between p-5 rounded-xl border transition-all text-left group cursor-pointer',
                  isSelected
                    ? 'border-primary bg-primary/10 shadow-[var(--surface-shadow-strong,0_4px_6px_-1px_rgb(0_0_0_/_0.1))] ring-2 ring-primary/20'
                    : 'border-border/60 bg-background/60 hover:bg-accent/50 hover:border-border'
                )}
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-muted text-muted-foreground border-border/50 group-hover:text-foreground'
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    {isSelected && (
                      <span className="text-[10px] font-bold [text-transform:var(--eyebrow-transform,uppercase)] [letter-spacing:var(--eyebrow-tracking,0.05em)] bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                        Active
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-foreground">{t.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {t.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export function NotificationSettingsSection() {
  const ctx = useOutletContext<SettingsOutletContext>();
  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      <CardHeader className="border-b border-border/50 bg-muted/30">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <CardTitle>Notifications</CardTitle>
        </div>
        <CardDescription>
          Choose what you are told about, and whether alerts reach you outside this tab.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <NotificationPreferencesPanel
          driver={ctx.pushDriver}
          showDiagnostics={ctx.showDiagnostics}
        />
      </CardContent>
    </Card>
  );
}

export function MembersSettingsSection() {
  const ctx = useOutletContext<SettingsOutletContext>();
  return (
    <WorkspaceMembersSection
      productKey={ctx.productKey}
      productLabel={ctx.productLabel}
      isAdmin={ctx.isAdmin}
      currentUserId={ctx.currentUserId}
    />
  );
}

export function FeaturesSettingsSection() {
  const ctx = useOutletContext<SettingsOutletContext>();
  if (!ctx.isAdmin) {
    return <Navigate to="../appearance" replace />;
  }
  return <FeaturesPanel />;
}

function FeaturesPanel() {
  const ctx = useOutletContext<SettingsOutletContext>();
  const utils = trpc.useUtils();
  const app = findAppByProductKey(ctx.productKey);
  const persona = getAppPersona(app?.key);

  const { data: flags, isLoading } = trpc.featureFlags.list.useQuery();
  const setEnabled = trpc.featureFlags.setEnabled.useMutation({
    onSuccess: (updated) => {
      utils.featureFlags.list.setData(undefined, updated);
      utils.contacts.getActivityFeed.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  if (isLoading || !flags) {
    return null;
  }

  return (
    <div className="space-y-6">
      {persona.entitySections.map((section) => (
        <Card
          key={section.title}
          className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden"
        >
          <CardHeader className="border-b border-border/50 bg-muted/30">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-primary" />
              <CardTitle>{section.title}</CardTitle>
            </div>
            <CardDescription>{section.description}</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {section.keys.map((key) => (
              <div key={key} className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {WORKSPACE_FEATURE_LABELS[key]}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {WORKSPACE_FEATURE_DESCRIPTIONS[key]}
                  </p>
                </div>
                <Switch
                  checked={flags[key]}
                  disabled={setEnabled.isPending}
                  onCheckedChange={(checked) =>
                    setEnabled.mutate({ featureKey: key, enabled: checked })
                  }
                />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Admin-only, matching `FeaturesSettingsSection`: only an admin can save/activate/remove a key. */
export function AiProviderKeysSettingsSection() {
  const ctx = useOutletContext<SettingsOutletContext>();
  if (!ctx.isAdmin) {
    return <Navigate to="../appearance" replace />;
  }
  return <AiProviderKeysSection />;
}

export function BillingSettingsSection() {
  const ctx = useOutletContext<SettingsOutletContext>();
  if (!ctx.isAdmin || !ctx.activeWorkspace?.organizationId) {
    return <Navigate to="../appearance" replace />;
  }
  return (
    <>
      <SubscriptionBillingPanel
        organizationId={ctx.activeWorkspace.organizationId}
        productKey={ctx.productKey}
        productLabel={ctx.productLabel}
        isAdmin={ctx.isAdmin}
      />
      <AiCreditsPanel productKey={ctx.productKey} isAdmin={ctx.isAdmin} />
      <InvoiceHistoryTable
        organizationId={ctx.activeWorkspace.organizationId}
        productKey={ctx.productKey}
      />
    </>
  );
}
